/**
 * 宿主页与被嵌入 iframe 之间的 postMessage 桥:事件(emit/on)与请求响应(call/onCall)两种语义。
 * 宿主端用 init(iframe),被嵌入端用 hostInit()。
 */
import sharedConfig from '../config'
import { isDebugMode } from './debug'

interface BridgeOptions {
  origin?: string
  /**
   * 校验入向消息的 event.origin,只接受该来源。不传则不校验(默认行为)。
   * iframe 内可能加载第三方页面时必须传:仅凭 event.source 判断挡不住第三方页伪造消息。
   */
  verifyOrigin?: string
  onload?: (payload?: any) => void
  onerror?: (e: any) => void
}

/**
 * 消息格式
 */
interface Message {
  type: string // 消息类型
  payload: any // 消息内容
  error?: any // 错误信息
  reqId?: string // 请求ID，用于请求发送
  resId?: string // 请求ID，用于响应请求
}

/**
 * 事件处理器类型
 */
type EventHandler = (payload: any) => void

/**
 * 通信桥接
 */
export class IframeBridge {
  private iframe: HTMLIFrameElement | null = null // iframe元素
  private messageHandlers: Map<string, Function> = new Map() // 请求处理器
  private eventHandlers: Map<string, Set<EventHandler>> = new Map() // 事件处理器
  private callEventHandler: Map<string, EventHandler> = new Map() // 调用事件处理器
  private target: Window | null = null // 消息发送目标窗口
  private targetOrigin: string = '*'
  private allowedOrigin: string | null = null // 入向消息的来源白名单，null 表示不校验
  private isConnected: boolean = false

  constructor() {}

  /**
   * 初始化
   * @param options 选项
   * @returns iframe元素
   */
  init(iframe: HTMLIFrameElement, options: BridgeOptions) {
    if (this.iframe) {
      this.destroy()
    }

    this.target = iframe!.contentWindow || null
    this.targetOrigin = sharedConfig.origin
    this.allowedOrigin = options.verifyOrigin ?? null
    this.iframe = iframe
    this.setupMessageListener()

    // 监听iframe加载完成事件
    this.on('ready', (payload) => {
      this.isConnected = true
      options.onload?.(payload)
    })

    iframe.onload = () => {}

    // 添加错误处理
    iframe.onerror = (e) => {
      console.error('Failed to load iframe')
      this.destroy()
      options.onerror?.(e)
    }
  }

  /**
   * host页面初始化
   * @param options 选项
   */
  hostInit(options: BridgeOptions) {
    this.target = window.parent

    // 设置消息发送的源
    const origin = options.origin
    if (origin) this.targetOrigin = origin

    this.isConnected = this.setupMessageListener()
    this.emit('ready')
  }

  /**
   * 设置消息监听器
   */
  private setupMessageListener() {
    if (!this.target) return false

    window.addEventListener('message', this.handleMessage)

    return true
  }

  /**
   * 处理消息
   * @param event 事件
   */
  private handleMessage = (event) => {
    if (event.source !== this.target) return
    if (this.allowedOrigin && event.origin !== this.allowedOrigin) return

    const message = event.data as Message

    if (isDebugMode('bridge')) {
      console.debug('[on message] :>> ', message)
    }

    // 请求call处理
    if (message.reqId) {
      this.dispatchCallEvent(message.type, message)
      return
    }

    // 响应call处理
    if (message.resId) {
      this.dispatchCallRes(message)
      return
    }

    this.dispatchEvent(message.type, message.payload)
  }

  /**
   * 发送消息
   */
  private postMessage(message: Message) {
    if (isDebugMode('bridge')) {
      console.debug('[postMessage] :>> ', message)
    }
    if (!this.target) return
    this.target.postMessage(message, this.targetOrigin)
  }

  /**
   * 发送消息并等待响应
   * @param type 消息类型
   * @param payload 消息数据
   * @returns Promise<T> 响应数据
   */
  public call<T>(type: string, payload?: any): Promise<T> {
    return new Promise((resolve, reject) => {
      const messageId = Math.random().toString(36).substring(2, 11)
      const message: Message = {
        type,
        payload,
        reqId: messageId,
      }

      const handler = (payload: any, error?: any) => {
        this.messageHandlers.delete(messageId)
        if (error) {
          reject(error)
        } else {
          resolve(payload)
        }
      }

      this.messageHandlers.set(messageId, handler)
      this.postMessage(message)
    })
  }

  /**
   * 分发调用响应
   * @param message 消息
   */
  private dispatchCallRes(message: Message) {
    const handler = this.messageHandlers.get(message.resId)

    if (handler) {
      try {
        handler(message.payload, message.error)
      } catch (e) {}
    }
  }

  /**
   * 调用方法
   * @param method 方法名
   * @param args 方法参数
   * @returns Promise<T> 响应数据
   */
  public callMethod<T>(method: string, ...args: any[]): Promise<T> {
    return this.call('method', { method, args })
  }

  /**
   * 发送消息（不等待响应）
   * @param type 消息类型
   * @param payload 消息数据
   */
  public emit(type: string, payload?: any) {
    this.postMessage({ type, payload })
  }

  /**
   * 注册事件监听器
   * @param type 事件类型
   * @param handler 事件处理器
   */
  public on(type: string, handler: EventHandler) {
    if (!this.eventHandlers.has(type)) {
      this.eventHandlers.set(type, new Set())
    }
    this.eventHandlers.get(type)!.add(handler)
  }

  /**
   * 注册调用事件监听器
   * @param type 事件类型
   * @param handler 事件处理器
   */
  public onCall(type: string, handler: EventHandler) {
    this.callEventHandler.set(type, handler)
  }

  /**
   * 移除事件监听器
   * @param type 事件类型
   * @param handler 事件处理器
   */
  public off(type: string, handler: EventHandler) {
    this.eventHandlers.get(type)?.delete(handler)
  }

  /**
   * 移除调用事件监听器
   * @param type 事件类型
   */
  public offCall(type: string) {
    this.callEventHandler.delete(type)
  }

  /**
   * 触发事件
   */
  private dispatchEvent(type: string, payload: any) {
    this.eventHandlers.get(type)?.forEach((handler) => {
      handler(payload)
    })
  }

  /**
   * 分发调用事件
   * @param type 事件类型
   * @param payload 事件数据
   * @returns 事件处理结果
   */
  private async dispatchCallEvent(type: string, message: Message) {
    const handler = this.callEventHandler.get(type)
    let result = null
    let error = null
    try {
      result = await handler?.(message.payload)
    } catch (e) {
      error = e
    }

    this.postMessage({ type, payload: result, error, resId: message.reqId })
  }

  /**
   * 销毁Bridge实例
   */
  public destroy() {
    if (this.iframe) {
      this.iframe.remove()
      this.iframe = null
    }
    window.removeEventListener('message', this.handleMessage)
    this.messageHandlers.clear()
    this.eventHandlers.clear()
    this.target = null
    this.allowedOrigin = null
  }
}
